import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { useLive } from '../../live.jsx';
import { Alert } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { AccountHead } from '../account/AccountLayout.jsx';
import { formatQty, money, shortDate, destination } from './requestUtils.js';
import './requests.css';

// My space › "Mes demandes d'achat / My buying requests": my requests
// (status, offers, new offers) and the offers my company sent.
export default function MyRequests() {
  const { t, lang, errorText } = useI18n();
  usePageTitle('buying.myTitle');
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'offers' ? 'offers' : 'requests';
  const [data, setData] = useState(null);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    if (tab === 'offers') api('/my/buying-requests/offers-sent').then((d) => setSent(d.items)).catch((e) => setError(errorText(e)));
    else api('/my/buying-requests').then(setData).catch((e) => setError(errorText(e)));
  }, [tab, errorText]);
  useEffect(() => { load(); }, [load]);
  useLive(['requests'], () => { load(); });

  const usage = data?.usage;
  const limitReached = usage && usage.max !== null && usage.used >= usage.max;

  return (
    <div className="acc-page">
      <AccountHead title={t('buying.myTitle')} lead={t('buying.myLead')}
        actions={<Link className="btn btn-primary" to="/account/buying-requests/new" aria-disabled={limitReached || undefined}>{t('buying.post')}</Link>} />
      <div className="acc-seg" role="tablist">
        {['requests', 'offers'].map((k) => <button key={k} type="button" role="tab" aria-selected={tab === k} aria-pressed={tab === k} onClick={() => setParams(k === 'offers' ? { tab: k } : {}, { replace: true })}>{t(`buying.myTabs.${k}`)}</button>)}
      </div>
      <Alert>{error}</Alert>

      {tab === 'requests' && (
        <>
          {usage && <p className="muted small">{usage.max === null ? t('buying.usageNoLimit', { used: usage.used }) : t('buying.usage', { used: usage.used, max: usage.max })}</p>}
          {limitReached && <p className="callout callout-warning">{t('buying.limitReached', { max: usage.max })}</p>}
          {!data && !error && <p className="muted">{t('common.loading')}</p>}
          {data && data.items.length === 0 && (
            <div className="acc-empty card"><Icon name="inbox" size={28} /><p className="strong">{t('buying.empty')}</p><p className="muted">{t('buying.emptyHint')}</p>
              <Link className="btn btn-primary" to="/account/buying-requests/new">{t('buying.postShort')}</Link></div>
          )}
          {data && data.items.length > 0 && (
            <ul className="req-list">
              {data.items.map((r) => (
                <li key={r.id}>
                  <Link className="req-row" to={`/account/buying-requests/${r.id}`}>
                    <span className="wanted-badge" aria-hidden="true">{t('buying.wanted')}</span>
                    <span className="req-row-main">
                      <strong>{r.original?.title || r.title}</strong>
                      <span className="req-row-meta">{formatQty(r.quantity, r.unit, lang, t)}{destination(r, lang) ? ` · ${destination(r, lang)}` : ''} · {t(r.open || r.status === 'pending' ? 'buying.expires' : 'buying.expired', { date: shortDate(r.expiresOn, lang) })}{r.anonymous ? ' · 🔒' : ''}</span>
                    </span>
                    <span className="req-row-side">
                      <span className={`chip-status req-status-${r.status}`}>{t(`buying.status.${r.status}`)}</span>
                      <span>{r.offers ? t('buying.offersN', { n: r.offers }) : t('buying.noOffers')}{r.newOffers > 0 && <> · <span className="acc-badge acc-badge-new">{t('buying.newOffersN', { n: r.newOffers })}</span></>}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {tab === 'offers' && (
        <>
          {!sent && !error && <p className="muted">{t('common.loading')}</p>}
          {sent && sent.length === 0 && <div className="acc-empty card"><p className="strong">{t('buying.noOffersSent')}</p><Link className="btn btn-ghost" to="/marketplace?tab=requests">{t('buying.title')}</Link></div>}
          {sent && sent.length > 0 && (
            <ul className="req-list">
              {sent.map((o) => (
                <li key={o.id}>
                  <Link className="req-row" to={`/marketplace/requests/${o.request.id}`}>
                    <span className="req-row-main">
                      <strong>{o.request.title}</strong>
                      <span className="req-row-meta">{money(o.price, o.currency, lang)} × {formatQty(o.quantity, o.unit, lang, t)}{o.incoterm ? ` · ${o.incoterm}` : ''} · {shortDate(o.updatedAt, lang)}{o.request.anonymous ? ` · ${t('buying.viaPme')}` : ''}</span>
                    </span>
                    <span className="req-row-side">
                      <span className={`chip-status offer-status-${o.status}`}>{t(`buying.offerStatus.${o.status}`)}</span>
                      <span className={`chip-status req-status-${o.request.status}`}>{t(`buying.status.${o.request.status}`)}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
